import React, { useState, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { format } from 'date-fns';
import { supabase } from '../../lib/supabase';
import 'react-day-picker/style.css';

export default function BlockDates() {
     const [equipment, setEquipment] = useState([]);
     const [selectedEquipment, setSelectedEquipment] = useState(null);
     const [blockedDates, setBlockedDates] = useState([]);
     const [selectedDates, setSelectedDates] = useState([]);
     const [reason, setReason] = useState('maintenance');
     const [loading, setLoading] = useState(true);
     const [blockError, setBlockError] = useState(null);
     const [saving, setSaving] = useState(false);

     useEffect(() => {
          fetchEquipment();
     }, []);

     useEffect(() => {
          if (selectedEquipment) {
               fetchBlockedDates(selectedEquipment);
          }
     }, [selectedEquipment]);

     async function fetchEquipment() {
          const { data } = await supabase
               .from('equipment')
               .select('*')
               .eq('is_active', true)
               .order('name');
          setEquipment(data || []);
          if (data && data.length > 0) {
               setSelectedEquipment(data[0].id);
          }
          setLoading(false);
     }

     async function fetchBlockedDates(equipmentId) {
          const { data } = await supabase
               .from('blocked_dates')
               .select('*')
               .eq('equipment_id', equipmentId)
               .order('date');
          setBlockedDates(data || []);
     }

     async function handleBlockDates() {
          if (selectedDates.length === 0 || !selectedEquipment) return;
          setBlockError(null);
          setSaving(true);

          const rows = selectedDates.map((date) => ({
               equipment_id: selectedEquipment,
               date: format(date, 'yyyy-MM-dd'),
               reason,
          }));

          const { data, error } = await supabase
               .from('blocked_dates')
               .insert(rows)
               .select();

          setSaving(false);

          if (error) {
               if (error.code === '23505') {
                    setBlockError(
                         'One or more of those dates is already blocked for this equipment. Check the "Currently Blocked" list and try again with different dates.'
                    );
               } else {
                    setBlockError(error.message || 'Failed to block dates.');
               }
               return;
          }

          if (!data || data.length === 0) {
               setBlockError(
                    'No rows were inserted. Your admin session may have expired — try signing out and back in.'
               );
               return;
          }

          setSelectedDates([]);
          fetchBlockedDates(selectedEquipment);
     }

     async function handleUnblock(id) {
          await supabase.from('blocked_dates').delete().eq('id', id);
          fetchBlockedDates(selectedEquipment);
     }

     const blockedDateObjects = blockedDates.map((b) => new Date(b.date));

     if (loading) {
          return <p className='text-darker/60'>Loading...</p>;
     }

     if (equipment.length === 0) {
          return (
               <div>
                    <h1 className='text-darker text-2xl font-bold mb-6'>Block Dates</h1>
                    <p className='text-darker/40'>
                         No active equipment found. Add equipment in the Inventory page first.
                    </p>
               </div>
          );
     }

     return (
          <div>
               <h1 className='text-darker text-2xl font-bold mb-6'>Block Dates</h1>

               {/* Equipment Selector */}
               <div className='mb-6'>
                    <label className='block text-darker/60 text-sm mb-2'>
                         Select Equipment
                    </label>
                    <div className='flex gap-2 flex-wrap'>
                         {equipment.map((item) => (
                              <button
                                   key={item.id}
                                   onClick={() => setSelectedEquipment(item.id)}
                                   className={`px-4 py-2 rounded-lg text-sm border duration-300 ${
                                        selectedEquipment === item.id
                                             ? 'border-yellow text-yellow bg-yellow/10'
                                             : 'border-gray-200 text-darker/40 hover:text-darker'
                                   }`}
                              >
                                   {item.name}
                              </button>
                         ))}
                    </div>
               </div>

               <div className='grid grid-cols-1 laptop:grid-cols-2 gap-6'>
                    {/* Calendar */}
                    <div className='bg-white rounded-xl p-5 border border-gray-200'>
                         <h3 className='text-yellow text-sm uppercase tracking-wider mb-4'>
                              Select Dates to Block
                         </h3>
                         <DayPicker
                              mode='multiple'
                              selected={selectedDates}
                              onSelect={setSelectedDates}
                              disabled={blockedDateObjects}
                              classNames={{
                                   root: 'text-darker',
                                   day: 'text-darker hover:bg-yellow/20 rounded-lg p-2',
                                   selected: 'bg-yellow text-black font-bold',
                                   disabled: 'text-red-400/50 line-through cursor-not-allowed',
                                   caption: 'text-yellow font-semibold text-lg',
                                   nav_button: 'text-yellow hover:text-darker',
                                   head_cell: 'text-darker/40 font-normal',
                              }}
                         />

                         {selectedDates.length > 0 && (
                              <div className='mt-4 pt-4 border-t border-gray-200 space-y-3'>
                                   <div>
                                        <label className='block text-darker/60 text-sm mb-1'>
                                             Reason
                                        </label>
                                        <select
                                             value={reason}
                                             onChange={(e) => setReason(e.target.value)}
                                             className='w-full bg-white border border-gray-200 rounded-lg px-4 py-2 text-darker focus:outline-none focus:border-yellow duration-300'
                                        >
                                             <option value='maintenance'>Maintenance</option>
                                             <option value='buffer'>Buffer Day</option>
                                             <option value='manual_block'>Manual Block</option>
                                        </select>
                                   </div>
                                   <button
                                        onClick={handleBlockDates}
                                        disabled={saving}
                                        className='w-full px-4 py-2 rounded-lg text-sm border border-yellow text-yellow hover:bg-yellow/10 duration-300 disabled:opacity-50 disabled:cursor-not-allowed'
                                   >
                                        {saving
                                             ? 'Blocking...'
                                             : `Block ${selectedDates.length} ${selectedDates.length === 1 ? 'Date' : 'Dates'}`}
                                   </button>
                                   {blockError && (
                                        <p className='text-red-700 text-sm bg-red-50 border border-red-200 rounded-lg p-3'>
                                             {blockError}
                                        </p>
                                   )}
                              </div>
                         )}
                    </div>

                    {/* Blocked Dates List */}
                    <div className='bg-white rounded-xl p-5 border border-gray-200'>
                         <h3 className='text-yellow text-sm uppercase tracking-wider mb-4'>
                              Currently Blocked
                         </h3>
                         {blockedDates.length === 0 ? (
                              <p className='text-darker/40 text-sm'>
                                   No dates blocked for this equipment.
                              </p>
                         ) : (
                              <div className='space-y-2 max-h-[400px] overflow-y-auto'>
                                   {blockedDates.map((blocked) => (
                                        <div
                                             key={blocked.id}
                                             className='flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2'
                                        >
                                             <div>
                                                  <span className='text-darker text-sm'>
                                                       {format(new Date(blocked.date), 'MMM d, yyyy')}
                                                  </span>
                                                  <span className='text-darker/30 text-xs ml-2 capitalize'>
                                                       {blocked.reason?.replace('_', ' ')}
                                                  </span>
                                             </div>
                                             <button
                                                  onClick={() => handleUnblock(blocked.id)}
                                                  className='text-red-400 text-xs hover:text-red-700 duration-300'
                                             >
                                                  Remove
                                             </button>
                                        </div>
                                   ))}
                              </div>
                         )}
                    </div>
               </div>
          </div>
     );
}
